import { useState, useEffect } from 'react';
import { Modal, Button, Form, ProgressBar, Badge, Row, Col, InputGroup } from 'react-bootstrap';
import { useApi } from '../../contexts/ApiProvider';

export default function MediaDetailModal({ show, onHide, item, onSuccess }) {
    const api = useApi();

    const [editForm, setEditForm] = useState({
        current_progress: 0,
        max_progress: 0, // <-- Add this
        status: 'In Progress',
        rating: 0,
        personal_notes: ''
    });

    useEffect(() => {
        if (item) {
            setEditForm({
                current_progress: item.current_progress || 0,
                max_progress: item.max_progress || 0, // <-- Add this
                status: item.status || 'In Progress',
                rating: item.rating || 0,
                personal_notes: item.personal_notes || ''
            });
        }
    }, [item]);

    if (!item) return null;

    const progressPercent = item.max_progress > 0
        ? (editForm.current_progress / item.max_progress) * 100
        : 0;

    const handleSave = async () => {
        const res = await api.put(`/media/${item.id}`, editForm);
        if (res.ok) {
            onSuccess(); // Triggers a refresh in the parent component
            onHide();
        } else {
            alert("Failed to save details.");
        }
    };

    const handleDelete = async () => {
        const proceed = window.confirm(`Are you sure you want to completely remove "${item.title}" from your tracker?`);
        if (!proceed) return;

        const res = await api.delete(`/media/${item.id}`);
        if (res.ok) {
            onSuccess(); // Refreshes the dashboard to remove the card
            onHide();    // Closes the modal
        } else {
            alert("Failed to delete item.");
        }
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton className="border-0 pb-0 pt-4 px-4">
                <Modal.Title className="fw-bold fs-4 text-dark">Track & Review</Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-4">
                <Row className="g-4">
                    {/* LEFT: Art & Meta */}
                    <Col md={4} className="text-center">
                        {item.cover_image_url ? (
                            <img src={item.cover_image_url} alt="Cover" className="rounded-3 shadow w-100 mb-3" style={{ objectFit: 'cover' }} />
                        ) : (
                            <div className="bg-secondary bg-opacity-25 rounded-3 w-100 mb-3 d-flex align-items-center justify-content-center" style={{ height: '250px' }}>
                                <i className="bi bi-book display-1 text-muted"></i>
                            </div>
                        )}
                        <h5 className="fw-bold mb-1">{item.title}</h5>
                        <p className="text-muted small mb-2">{item.metadata?.authors?.join(', ')}</p>
                        <Badge bg="primary">{item.media_type}</Badge>
                    </Col>

                    {/* RIGHT: Editor */}
                    <Col md={8}>
                        {/* Progress Section */}
                        <div className="bg-light p-3 rounded-4 mb-4">
                            <div className="d-flex justify-content-between small fw-bold mb-2">
                                <span className="text-primary text-uppercase letter-spacing-1">Progress</span>
                                <span>{Math.round(progressPercent)}%</span>
                            </div>
                            <ProgressBar now={progressPercent} className="mb-3" style={{ height: '8px' }} />

                            <Row className="g-3 align-items-center mb-3">
                                <Col sm={5}>
                                    <Form.Select
                                        value={editForm.status}
                                        onChange={e => {
                                            const newStatus = e.target.value;
                                            const updates = { status: newStatus };

                                            // Auto-fill logic!
                                            const today = new Date().toISOString().split('T')[0];
                                            if (newStatus === 'Completed' && !editForm.end_date) updates.end_date = today;
                                            if (newStatus === 'In Progress' && !editForm.start_date) updates.start_date = today;

                                            setEditForm({...editForm, ...updates});
                                        }}
                                        className="fw-bold"
                                    >
                                        <option value="Backlog">Backlog</option>
                                        <option value="In Progress">In Progress</option>
                                        <option value="Completed">Completed</option>
                                        <option value="Dropped">Dropped</option>
                                    </Form.Select>
                                </Col>

                                {/* SMART INPUTS: Hours/Mins vs Standard */}
                                <Col sm={7}>
                                    {item.media_type === 'Audiobook' || item.media_type === 'Movie' ? (
                                        <div className="d-flex align-items-center gap-2">
                                            {/* CURRENT HOURS & MINS */}
                                            <InputGroup size="sm">
                                                <Form.Control type="number" placeholder="Hr" className="text-center" style={{ minWidth: '65px' }} value={Math.floor(editForm.current_progress / 60) || ''} onChange={e => setEditForm({...editForm, current_progress: ((parseInt(e.target.value) || 0) * 60) + (editForm.current_progress % 60)})} />
                                                <InputGroup.Text className="px-1 text-muted">h</InputGroup.Text>
                                                <Form.Control type="number" placeholder="Min" className="text-center" style={{ minWidth: '65px' }} value={editForm.current_progress % 60 || ''} onChange={e => setEditForm({...editForm, current_progress: (Math.floor(editForm.current_progress / 60) * 60) + (parseInt(e.target.value) || 0)})} />
                                                <InputGroup.Text className="px-1 text-muted">m</InputGroup.Text>
                                            </InputGroup>

                                            <span className="text-muted fw-bold">/</span>

                                            {/* MAX HOURS & MINS */}
                                            <InputGroup size="sm">
                                                <Form.Control type="number" placeholder="Hr" className="text-center" style={{ minWidth: '65px' }} value={Math.floor(editForm.max_progress / 60) || ''} onChange={e => setEditForm({...editForm, max_progress: ((parseInt(e.target.value) || 0) * 60) + (editForm.max_progress % 60)})} />
                                                <InputGroup.Text className="px-1 text-muted">h</InputGroup.Text>
                                                <Form.Control type="number" placeholder="Min" className="text-center" style={{ minWidth: '65px' }} value={editForm.max_progress % 60 || ''} onChange={e => setEditForm({...editForm, max_progress: (Math.floor(editForm.max_progress / 60) * 60) + (parseInt(e.target.value) || 0)})} />
                                                <InputGroup.Text className="px-1 text-muted">m</InputGroup.Text>
                                            </InputGroup>
                                        </div>
                                    ) : (
                                        <div className="d-flex align-items-center gap-2">
                                            <Form.Control type="number" value={editForm.current_progress} onChange={e => setEditForm({...editForm, current_progress: parseInt(e.target.value) || 0})} />
                                            <span className="fw-bold text-muted">/</span>
                                            <Form.Control type="number" value={editForm.max_progress} onChange={e => setEditForm({...editForm, max_progress: parseInt(e.target.value) || 0})} />
                                            <span className="small fw-bold text-muted flex-shrink-0">{item.progress_unit}</span>
                                        </div>
                                    )}
                                </Col>
                            </Row>

                            {/* DATES */}
                            <Row className="g-2">
                                <Col sm={6}>
                                    <Form.Group>
                                        <Form.Label className="small fw-bold text-muted mb-0">Start Date</Form.Label>
                                        <Form.Control type="date" size="sm" value={editForm.start_date || ''} onChange={e => setEditForm({...editForm, start_date: e.target.value})} />
                                    </Form.Group>
                                </Col>
                                <Col sm={6}>
                                    <Form.Group>
                                        <Form.Label className="small fw-bold text-muted mb-0">End Date</Form.Label>
                                        <Form.Control type="date" size="sm" value={editForm.end_date || ''} onChange={e => setEditForm({...editForm, end_date: e.target.value})} />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </div>

                        {/* Review Section */}
                        <Form.Group className="mb-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <Form.Label className="small fw-bold text-muted text-uppercase letter-spacing-1 mb-0">Your Rating</Form.Label>
                                <div className="text-warning fs-5">
                                    {[1, 2, 3, 4, 5].map(star => (
                                        <i
                                            key={star}
                                            className={`bi bi-star${star <= editForm.rating ? '-fill' : ''} me-1 cursor-pointer`}
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => setEditForm({...editForm, rating: star})}
                                        ></i>
                                    ))}
                                </div>
                            </div>
                        </Form.Group>

                        <Form.Group>
                            <Form.Label className="small fw-bold text-muted text-uppercase letter-spacing-1">Journal & Notes</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={5}
                                placeholder="What did you think? Favorite quotes? Takeaways?"
                                value={editForm.personal_notes}
                                onChange={e => setEditForm({...editForm, personal_notes: e.target.value})}
                                className="bg-light border-0 rounded-3 p-3"
                            />
                        </Form.Group>
                    </Col>
                </Row>
            </Modal.Body>
            <Modal.Footer className="border-0 p-4 pt-0 d-flex justify-content-between">
                {/* Left Side: Delete Button */}
                <Button variant="outline-danger" className="rounded-pill px-3 shadow-sm border-opacity-50" onClick={handleDelete} title="Delete Item">
                    <i className="bi bi-trash"></i> Delete
                </Button>

                {/* Right Side: Action Buttons */}
                <div>
                    <Button variant="light" className="rounded-pill px-4 fw-bold me-2" onClick={onHide}>Cancel</Button>
                    <Button variant="success" className="rounded-pill px-5 fw-bold shadow-sm" onClick={handleSave}>Save Journal</Button>
                </div>
            </Modal.Footer>
        </Modal>
    );
}
